import { NAV_ROUTES } from '@/utils/constants';

/**
 * Reads invitation id and type from a flat `URLSearchParams` instance (raw query string).
 * Accepts `invitationId` as an alias for `hash` for backward compatibility.
 *
 * @param searchParams - e.g. from `window.location.search`, or the inner query after parsing `continueUrl` from an email link
 * @returns `{ hash, invitationType }` or `null` if required keys are missing, empty, or `invitationType` is not allowed
 */
function parseInvitationFromSearchParams(
  searchParams: URLSearchParams,
): { hash: string; invitationType: 'organization' | 'workspace' } | null {
  const hash = searchParams.get('hash') ?? searchParams.get('invitationId');
  const invitationType = searchParams.get('invitationType');
  if (!hash || !invitationType) return null;
  if (invitationType !== 'organization' && invitationType !== 'workspace') return null;
  return { hash, invitationType };
}

/**
 * Parses `href` into a `URL` and returns only the **query string** (`?a=1&b=2`) as `URLSearchParams`.
 * Uses a dummy base `https://example.com`, so both absolute (`https://…`) and relative (`/path?x=1`) hrefs work;
 * for absolute hrefs the base is ignored per `new URL()` rules.
 *
 * @param href - raw URL string (e.g. the `continueUrl` value from a Firebase verification email link)
 * @returns Parsed `searchParams`, or `null` if the string is not a valid URL for this parser
 */
function searchParamsFromHref(href: string): URLSearchParams | null {
  try {
    return new URL(href, 'https://example.com').searchParams;
  } catch {
    return null;
  }
}

/**
 * Percent-decodes a string once via `decodeURIComponent` (e.g. `%20` → space).
 * Used for a second parse attempt when the entire `continueUrl` is still encoded in the message
 * (double encoding from some email providers).
 *
 * @param percentEncoded - same candidate string that may be passed to `searchParamsFromHref`
 * @returns The decoded string only if it **differs** from the input; otherwise `null` (or `null` if decoding throws)
 */
function tryDecodeOnce(percentEncoded: string): string | null {
  try {
    const d = decodeURIComponent(percentEncoded);
    return d === percentEncoded ? null : d;
  } catch {
    return null;
  }
}

/**
 * Extracts the query of the **inner** URL from Firebase’s `continueUrl` string, where Firebase wraps the
 * `url` passed to `sendEmailVerification`. That inner URL carries `hash` / `invitationId` and `invitationType`.
 * Tries parsing the raw string first, then once-decoded input if needed.
 *
 * @param continueUrl - value of the `continueUrl` query param (not the full action link with `mode=verifyEmail&oobCode=…`)
 * @returns `URLSearchParams` for the inner URL’s query, or `null` if nothing could be parsed
 */
function getSearchParamsFromContinueUrl(continueUrl: string): URLSearchParams | null {
  const afterDecode = tryDecodeOnce(continueUrl);
  return (
    searchParamsFromHref(continueUrl) ??
    (afterDecode ? searchParamsFromHref(afterDecode) : null)
  );
}

/**
 * Resolves invitation data (`hash` + `invitationType`) from the current page’s query **or** from a nested
 * `continueUrl` on Firebase verification-email links. In those emails, custom params are not siblings of
 * `oobCode` — Firebase embeds the full continue URL in `continueUrl`, which requires this second code path.
 *
 * @param searchParams - usually from the page location query (e.g. after email verification on `/verify-email/…`)
 * @returns Normalized invitation data, or `null` if the URL (including any `continueUrl`) does not contain it
 */
export function parseInvitationUrlParams(
  searchParams: URLSearchParams,
): { hash: string; invitationType: 'organization' | 'workspace' } | null {
  const direct = parseInvitationFromSearchParams(searchParams);
  if (direct) return direct;
  const continueUrl = searchParams.get('continueUrl');
  if (!continueUrl) return null;
  const inner = getSearchParamsFromContinueUrl(continueUrl);
  if (!inner) return null;
  return parseInvitationFromSearchParams(inner);
}

/**
 * Returns a **canonical** query string for links: only `hash` and `invitationType` (maps `invitationId` to `hash`).
 * Use when building the `url` for `sendEmailVerification` or when resending with the same invitation context.
 *
 * @param searchParams - current page params (may only expose nested data via `continueUrl`; see `parseInvitationUrlParams`)
 * @returns e.g. `hash=…&invitationType=…`, or `null` if no invitation is present in the URL
 */
export function getInvitationQueryString(searchParams: URLSearchParams): string | null {
  const parsed = parseInvitationUrlParams(searchParams);
  if (!parsed) return null;
  return new URLSearchParams({
    hash: parsed.hash,
    invitationType: parsed.invitationType,
  }).toString();
}

/**
 * Returns the app **relative** path to the invitation flow with the same query as `getInvitationQueryString`
 * (suitable for `Link`, `navigate`, or `redirect` after email verification or login with `redirectUrl`).
 *
 * @param searchParams - same as `getInvitationQueryString`
 * @returns e.g. `/handleInvitation?hash=…&invitationType=…`, or `null` if invitation data is missing
 */
export function getHandleInvitationUrlFromSearchParams(
  searchParams: URLSearchParams,
): string | null {
  const qs = getInvitationQueryString(searchParams);
  if (!qs) return null;
  return `${NAV_ROUTES.handleInvitation}?${qs}`;
}
